<template>
  <div class="content overlay__inner" :class="formClass">
    <div class="heading-bar">
      <div class="heading-bar__actions">
        <button type="button" class="close-icon" @click="close">&times;</button>
      </div>
      <h1 v-if="!editMode" class="hdg hdg--3">Add availability</h1>
      <h1 v-else-if="isRemoving" class="hdg hdg--3">Remove availability</h1>
      <h1 v-else class="hdg hdg--3">Edit availability</h1>
    </div>
    <p v-if="isRepeating" class="mb-24">
      <CalendarIconRecurring class="inline-block mr-4 align-[-0.125em]" :width="16" :height="16" color="currentColor" />
      {{ repeatingSubtitle }}
    </p>

    <form @submit.prevent="submitForm">
      <div v-if="showScopeRadios" class="form__repeating">
        <div class="form__repeat-group">
          <poppins-radio-button
            id="edit_scope_occurrence"
            v-model="editScope"
            radio-name="edit_scope"
            label-name="This occurrence"
            :radio-value="AvailabilityEditScope.SINGLE"
            :checked="editScope === AvailabilityEditScope.SINGLE"
          />
        </div>

        <div class="form__repeat-group">
          <poppins-radio-button
            id="edit_scope_following"
            v-model="editScope"
            radio-name="edit_scope"
            label-name="This and following occurrences"
            :radio-value="AvailabilityEditScope.ALL_RECURRING"
            :checked="editScope === AvailabilityEditScope.ALL_RECURRING"
          />
        </div>

        <AlertBanner
          v-if="showFollowingOccurrencesWarning"
          class="mt-16 mb-24 rounded-xl"
          :status="helperStatus.Warn"
          :auto-scroll-to-banner="false"
          :hide-icon="true"
        >
          Any interviews booked in this range will remain scheduled.
        </AlertBanner>
      </div>

      <div v-else class="form__section">
        <div class="form__group">
          <div class="form__group-item mb-24">
            <poppins-date-picker
              id="form-date"
              v-model="sittingDate"
              label-name="Available date"
              :max-date="maxStartDate"
              :status="sittingDateStatus"
              :helper-text="sittingDateHelperText"
              :custom-classes="'w-full'"
              @manual-input="validateTypedInput"
            />
          </div>
        </div>

        <div class="form__group">
          <div class="form__group-item form__group-item--col">
            <poppins-time-picker
              id="form-start-date"
              v-model="startTime"
              label-name="Start time"
              :start="startRange[0]"
              :end="startRange[1]"
              :status="startTimeStatus"
              :helper-text="startTimeHelperText"
            />
          </div>

          <div class="form__group-item form__group-item--col">
            <poppins-time-picker
              id="regular-schedule-end"
              v-model="endTime"
              label-name="End time"
              :start="endRange[0]"
              :end="endRange[1]"
              :status="endTimeStatus"
              :helper-text="endTimeHelperText"
            />
          </div>
        </div>

        <HelperText id="availability-home-time-zone" :text="`Times are in ${homeTimeZoneLabel}.`" class="mb-24" />
      </div>

      <div v-if="!editMode" class="form__repeat-group">
        <poppins-checkbox
          v-model="repeating"
          value="repeat"
          checkbox-id="repeat"
          label-name="Repeat this time block"
          label-class="mb-0"
        />
      </div>

      <div v-if="!editMode && repeating" class="form__repeating">
        <div class="form__repeat-group">
          <poppins-radio-button
            id="repeat_on_weekly"
            v-model="repeatOn"
            radio-name="repeat_on"
            :label-name="`Weekly on ${repeatDay}`"
            radio-value="weekly"
            :checked="repeatOn === 'weekly'"
          />
        </div>

        <div class="form__repeat-group">
          <poppins-radio-button
            id="repeat_on_daily"
            v-model="repeatOn"
            :disabled="!showEveryWeekday || null"
            radio-name="repeat_on"
            label-name="Every weekday (Mon to Fri)"
            radio-value="weekdays"
            :checked="repeatOn === 'weekdays'"
          />
          <p v-if="errors['repetition']" class="form__error">{{ errors['repetition'] }}</p>
        </div>

        <div class="form__repeat-group">
          <p class="form__notice">This will be repeated for about 90 days.</p>
        </div>
      </div>

      <div class="form__section form__section--flex-buttons">
        <template v-if="isRemoving">
          <Button
            value="Cancel"
            variant="outlined"
            size="medium"
            class="w-full"
            :disabled="deleting"
            @click.prevent="cancelScopeStep"
          />
          <Button value="Delete" variant="primary" size="medium" class="w-full" :loading="deleting" @click.prevent="clear" />
        </template>
        <template v-else-if="isUpdating">
          <Button
            value="Cancel"
            variant="outlined"
            size="medium"
            class="w-full"
            :disabled="saving"
            @click.prevent="cancelScopeStep"
          />
          <Button value="Update" variant="primary" size="medium" type="submit" class="w-full" :loading="saving" />
        </template>
        <template v-else-if="editMode">
          <Button
            value="Delete"
            variant="outlined"
            size="medium"
            class="w-full"
            :loading="deleting"
            :disabled="saving"
            @click.prevent="clear"
          />
          <Button
            value="Update"
            variant="primary"
            size="medium"
            type="submit"
            class="w-full"
            :loading="saving"
            :disabled="deleting"
          />
        </template>
        <template v-else>
          <Button value="Save" variant="primary" size="medium" type="submit" class="w-full" :loading="saving" />
        </template>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import { add, addDays, addMinutes, endOfDay, format, intervalToDuration, isValid, parseISO, set, startOfDay } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import helperStatus from '@/helpers/status';
import PoppinsDatePicker from '@/components/PoppinsDatePicker/PoppinsDatePicker.vue';
import PoppinsTimePicker from '@/components/timepicker/PoppinsTimePicker.vue';
import Button from '@/components/button/Button.vue';
import Checkbox from '@/components/checkbox/Checkbox.vue';
import RadioButton from '@/components/radio_button/RadioButton.vue';
import HelperText from '@/components/helper_text/HelperText.vue';
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import CalendarIconRecurring from '@/graphics/icons/CalendarIconRecurring.vue';
import { AvailabilityEditScope, AvailabilityPendingAction, DEFAULT_HOME_TIME_ZONE } from '@/lib/availability';

export default {
  name: 'AvailabilityForm',
  components: {
    PoppinsDatePicker,
    PoppinsTimePicker,
    Button,
    PoppinsCheckbox: Checkbox,
    PoppinsRadioButton: RadioButton,
    HelperText,
    AlertBanner,
    CalendarIconRecurring,
  },
  props: {
    availability: {
      type: Object,
      default: null,
    },
    formClass: {
      type: String,
      default: '',
    },
    timeZone: {
      type: String,
      default: DEFAULT_HOME_TIME_ZONE,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    deleting: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['submit', 'submitRepeating', 'update', 'clear', 'close'],
  data(): {
    errors: any;
    sittingDate: Date | null;
    startTime: Date | null;
    endTime: Date | null;
    startRange: Date[];
    endRange: Date[];
    originalClearStart: string | null;
    originalClearEnd: string | null;
    repeating: boolean;
    repeatOn: string;
    editScope: AvailabilityEditScope;
    pendingAction: AvailabilityPendingAction | null;
    sittingDateHelperText: string;
    endTimeHelperText: string;
    startTimeHelperText: string;
    helperStatus: typeof helperStatus;
  } {
    const startIso = this.availability?.node?.start ?? null;
    const endIso = this.availability?.node?.end ?? null;
    const start = startIso ? toZonedTime(parseISO(startIso), this.timeZone) : null;
    const end = endIso ? toZonedTime(parseISO(endIso), this.timeZone) : null;

    return {
      errors: {},
      sittingDate: start ? startOfDay(start) : addDays(new Date(), 2),
      startTime: start,
      endTime: end,
      startRange: [],
      endRange: [],
      originalClearStart: startIso,
      originalClearEnd: endIso,
      repeating: false,
      repeatOn: 'weekly',
      editScope: AvailabilityEditScope.SINGLE,
      pendingAction: null,
      sittingDateHelperText: '',
      endTimeHelperText: '',
      startTimeHelperText: '',
      helperStatus,
    };
  },
  computed: {
    AvailabilityEditScope(): typeof AvailabilityEditScope {
      return AvailabilityEditScope;
    },
    homeTimeZoneLabel(): string {
      return formatInTimeZone(new Date(), this.timeZone, 'zzzz');
    },
    maxStartDate(): Date {
      const today = new Date();
      const thirtyDaysFromToday = new Date();
      // returning the code below will return a number, don't do it, you need to return a date
      thirtyDaysFromToday.setDate(today.getDate() + 30);

      // returning the variable like this will return a date
      return thirtyDaysFromToday;
    },
    editMode(): boolean {
      return !!this.availability;
    },
    isRepeating(): boolean {
      return !!this.availability?.node?.repeatingAvailability?.id;
    },
    showScopeRadios(): boolean {
      return this.isRepeating && !!this.pendingAction;
    },
    showFollowingOccurrencesWarning(): boolean {
      return this.editScope === AvailabilityEditScope.ALL_RECURRING;
    },
    isRemoving(): boolean {
      return this.pendingAction === AvailabilityPendingAction.DELETE;
    },
    isUpdating(): boolean {
      return this.pendingAction === AvailabilityPendingAction.UPDATE;
    },
    repeatingWeekday(): string {
      if (!this.sittingDate) return '';
      return format(this.sittingDate, 'eeee');
    },
    repeatingSubtitle(): string {
      const schedule = this.availability?.node?.repeatingAvailability?.schedule;
      if (schedule === 'WEEKDAYS') {
        return 'Repeats every weekday (Mon to Fri)';
      }
      return `Repeats weekly on ${this.repeatingWeekday}s`;
    },
    sittingDateStatus(): helperStatus {
      if (this.sittingDateHelperText) {
        return helperStatus.Error;
      }
      return helperStatus.Neutral;
    },
    startTimeStatus(): helperStatus {
      if (this.startTimeHelperText) {
        return helperStatus.Error;
      }
      return helperStatus.Neutral;
    },
    endTimeStatus(): helperStatus {
      if (this.endTimeHelperText) {
        return helperStatus.Error;
      }
      return helperStatus.Neutral;
    },
    repeatDay(): string {
      if (!this.sittingDate) return '';

      return format(this.sittingDate, 'eee');
    },
    showEveryWeekday(): boolean {
      if (!this.sittingDate) return false;

      const d = this.sittingDate.getDay();
      return d > 0 && d < 6;
    },
    busy(): boolean {
      return this.saving || this.deleting;
    },
  },
  watch: {
    sittingDate(value: Date | null) {
      this.sittingDateHelperText = '';

      if (value === null) {
        this.startTime = null;
        this.endTime = null;
        this.calculateStartRange();
        this.calculateEndRange();
        return;
      }

      if (this.startTime) {
        const oldStart = this.startTime;
        this.startTime = set(this.startTime, {
          year: value.getFullYear(),
          month: value.getMonth(),
          date: value.getDate(),
        });

        // keep the original interval by shifting the end time along with the start
        if (this.endTime) {
          const interval = intervalToDuration({ start: oldStart, end: this.endTime });
          this.endTime = add(this.startTime, interval);
        }
      }

      // Weekday repetition is only valid Mon–Fri; fall back to weekly on weekends.
      if (this.repeatOn === 'weekdays' && !this.showEveryWeekday) {
        this.repeatOn = 'weekly';
      }

      this.calculateStartRange();
    },
    startTime(newStart: Date | null, oldStart: Date | null) {
      this.startTimeHelperText = '';

      if (newStart && newStart.getTime() !== oldStart?.getTime()) {
        this.calculateEndRange();
      }
    },
    endTime() {
      this.endTimeHelperText = '';
    },
  },
  created(): void {
    this.calculateStartRange();
    this.calculateEndRange();
  },
  methods: {
    calculateStartRange(): void {
      if (!this.sittingDate) {
        this.startRange = [];
        return;
      }

      this.startRange = [startOfDay(this.sittingDate), endOfDay(this.sittingDate)];
    },
    calculateEndRange(): void {
      if (!this.startTime) {
        this.endRange = [];
        return;
      }

      // Always refresh the range from the current start so the end options never go
      // stale. The chosen endTime is kept as-is (PoppinsTimePicker keeps it visible
      // even when it falls outside this range), and validate() flags end <= start on save.
      this.endRange = [addMinutes(this.startTime, 30), addDays(this.startTime, 1)];
    },
    submitForm() {
      if (this.busy || this.isRemoving) return;

      if (this.availability) {
        return this.isRepeating ? this.submitRepeatingUpdate() : this.submitSingleUpdate();
      }
      return this.repeating ? this.submitRepeatingCreate() : this.submitSingleCreate();
    },
    submitSingleCreate() {
      if (!this.validate()) return;
      this.$emit('submit', this.startTime, this.endTime);
    },
    submitRepeatingCreate() {
      if (!this.validate()) return;
      this.$emit('submitRepeating', this.startTime, this.endTime, this.repeatOn.toUpperCase());
    },
    submitSingleUpdate() {
      if (!this.validate()) return;
      this.$emit(
        'update',
        { clearStart: this.originalClearStart, clearEnd: this.originalClearEnd },
        this.startTime,
        this.endTime
      );
    },
    submitRepeatingUpdate() {
      if (!this.pendingAction) {
        if (!this.validate()) return;
        this.pendingAction = AvailabilityPendingAction.UPDATE;
        return;
      }
      this.$emit(
        'update',
        { clearStart: this.originalClearStart, clearEnd: this.originalClearEnd },
        this.startTime,
        this.endTime,
        this.editScope
      );
    },
    clear() {
      if (this.busy) return;

      // If we are dealing with a repeating block, we need to handle the confirmation step.
      if (this.isRepeating) {
        switch (this.pendingAction) {
          case AvailabilityPendingAction.DELETE:
            // If the action is DELETE, this is the confirmation step.
            // Emit the clear event with the chosen scope.
            this.$emit('clear', this.availability, this.editScope);
            break;
          default:
            // If the action is anything else
            // Set the action to DELETE and show confirmation radios to select scope.
            this.pendingAction = AvailabilityPendingAction.DELETE;
            break;
        }

        // Exit the function early to avoid deleting the block immediately.
        return;
      }

      // One-off block: delete immediately.
      this.$emit('clear', this.availability);
    },
    cancelScopeStep() {
      this.pendingAction = null;
      this.editScope = AvailabilityEditScope.SINGLE;
    },
    // TODO: There will definitely be a better way of doing validation.
    // TODO: We will need to check with product about the correct messages.
    validate(): boolean {
      const e: any = {};

      if (!this.sittingDate) this.sittingDateHelperText = 'This field is required';
      if (!isValid(this.startTime)) this.startTimeHelperText = 'This field is required';
      if (!isValid(this.endTime)) this.endTimeHelperText = 'This field is required';

      if (this.endTime && this.startTime && this.endTime <= this.startTime) {
        this.endTimeHelperText = 'Choose an end time later than the start time.';
      }

      // This shouldn't happen because repeatOn is switched when a weekend is selected, but it's an extra safety check.
      if (!this.showEveryWeekday && this.repeatOn === 'weekdays') e.repetition = 'Weekday repetition cannot be used on a weekend';

      this.errors = e;

      if (this.sittingDateHelperText || this.startTimeHelperText || this.endTimeHelperText) return false;
      if (Object.keys(e).length !== 0) return false;

      return true;
    },
    validateTypedInput(dateString): void {
      const date = new Date(dateString);
      const today = new Date();

      if (date < today || date > this.maxStartDate) {
        this.sittingDate = null;
        this.sittingDateHelperText = 'Please enter a date within a month of today.';
        return;
      }

      this.sittingDate = date;
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>
